import { FormOptions } from '@Modules/ui_module/_models';
import { UI_CONST } from '@Modules/ui_module/display';
import { Logger, LoggerService } from '@Shared/logger';
export class Form {
  private Logger: Logger = LoggerService.createLogger('Form');

  private form: HTMLFormElement;
  private formOptions: FormOptions;

  private errorMessages: { [key: string]: string } = {
    required: 'This field is required.',
    minLength: 'Please enter at least {minLength} characters.',
    email: 'Please enter a valid email address.',
    passwordMismatch: 'Passwords do not match.',
  };

  private _submitButtonText: string = '';
  private _submitButtonProcessingText: string = '';
  constructor(_formOptions: FormOptions) {
    this.Logger.trace('****  CTOR ****');
    const formElement = document.querySelector(
      UI_CONST.formSelector,
    ) as HTMLFormElement;
    if (formElement instanceof HTMLFormElement) {
      this.Logger.trace('Found form element:', formElement);
      this.form = formElement;
      this.formOptions = _formOptions;
      this._submitButtonText = this.formOptions.submitButton.text || 'Submit';
      this._submitButtonProcessingText =
        this.formOptions.submitButton.processingText || 'Processing...';
      this.setup();
    } else {
      this.Logger.warn(
        'Form element not found or not a valid HTMLFormElement.',
      );
    }
  }
  setup(): void {
    this.Logger.trace('setup', this.form);
    this.addInputListeners();
  }

  destroy(): void {
    this.Logger.trace('destroy');
  }

  enable(): void {
    this.Logger.trace('enable');
    this.toggleInputEnabled(true);
    this.toggleSubmitButtonStatus(true);
    this.toggleAnchorEnabled(true);
    this.toggleModalCloseStatus(true);
    this.toggleSubmitButtonEnabled(true);
    this.toggleSocialButtonStatus(true);
  }

  disable(): void {
    this.Logger.trace('disable');
    this.toggleInputEnabled(false);
    this.toggleSubmitButtonStatus(false);
    this.toggleAnchorEnabled(false);
    this.toggleModalCloseStatus(false);
    this.toggleSubmitButtonEnabled(false);
    this.toggleSocialButtonStatus(false);
  }

  onSubmit<T>(callback: (data: T) => void) {
    this.Logger.trace('onSubmit', this.form);

    const handler = (event) => {
      event.preventDefault(); // Prevent the default form submission
      if (!this.validate()) {
        this.Logger.warn('Form is not valid.');
        return;
      }

      const formData = new FormData(this.form);
      const data: any = {};
      formData.forEach((value, key) => {
        data[key] = value;
      });
      this.disable(); // Disable the form to prevent multiple submissions
      this.Logger.trace('Form submitted with data:', data);
      callback(data); // Call the provided callback with the form data
    };

    this.form.addEventListener('submit', handler);
    return () => {
      this.Logger.trace('Removing submit event listener');
      this.form.removeEventListener('submit', handler);
    };
  }

  private addInputListeners() {
    // Add event listeners to check validation as the user types
    const inputs = this.form.querySelectorAll('input');
    inputs.forEach((input: HTMLInputElement) => {
      const doValidation = () => {
        this.clearError(input);
        this.validateInput(input);
      };

      let typingTimer: any;
      const doneTypingInterval = 500;

      // validate `doneTypingInterval` after last keystroke
      input.addEventListener('keyup', function () {
        clearTimeout(typingTimer);
        typingTimer = setTimeout(doValidation, doneTypingInterval);
      });

      // clear `typingTimer` since user is still typing
      input.addEventListener('keydown', function () {
        clearTimeout(typingTimer);
      });

      // validate on blur and clear `typingTimer`
      input.addEventListener('blur', () => {
        clearTimeout(typingTimer);
        doValidation();
      });
    });
  }

  public setFieldError(fieldName: string, message?: string): void {
    const input = this.form.querySelector<HTMLInputElement>(
      `[name="${fieldName}"]`,
    );
    if (!input) {
      return;
    }

    this.form.classList.add('was-validated');
    input.classList.remove('is-valid');
    input.classList.add('is-invalid');
    input.setCustomValidity(message || 'Invalid');

    const errorDiv = input.nextElementSibling as HTMLDivElement;
    if (errorDiv && errorDiv.tagName === 'DIV' && message) {
      errorDiv.textContent = message;
      errorDiv.style.display = 'block';
    }
  }

  public clearFieldError(fieldName: string): void {
    const input = this.form.querySelector<HTMLInputElement>(
      `[name="${fieldName}"]`,
    );
    if (!input) {
      return;
    }

    input.setCustomValidity('');
    this.clearError(input);
    if (input.value) {
      input.classList.add('is-valid');
    }
  }

  private toggleInputEnabled(enabled: boolean): void {
    const formFields = this.form.querySelectorAll<HTMLElement>(
      'input, textarea, select',
    );
    formFields.forEach((field) => {
      (
        field as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      ).disabled = !enabled;
    });
  }

  private toggleModalCloseStatus(enabled: boolean): void {
    const modal = this.form.closest('.modal');
    if (modal) {
      if (!enabled) {
        modal
          .querySelectorAll('[data-bs-dismiss="modal"]')
          .forEach((closeButton) => {
            (closeButton as HTMLElement).style.pointerEvents = 'none';
          });
      } else {
        modal
          ?.querySelectorAll('[data-bs-dismiss="modal"]')
          .forEach((closeButton) => {
            (closeButton as HTMLElement).style.pointerEvents = '';
          });
      }
    }
  }

  private toggleAnchorEnabled(enabled: boolean): void {
    const toggleScreenLinks = this.form.querySelectorAll<HTMLAnchorElement>(
      '.a-disable-on-submit',
    );
    if (toggleScreenLinks) {
      toggleScreenLinks.forEach((toggleScreenLink) => {
        toggleScreenLink.style.pointerEvents = enabled ? '' : 'none'; // Disable interaction
        toggleScreenLink.style.opacity = enabled ? '' : '0.5'; // Visual cue
        toggleScreenLink.style.cursor = enabled ? '' : 'not-allowed'; // Disabled cursor
        toggleScreenLink.dataset.disabled = enabled ? '' : 'true';
      });
    }
  }

  private toggleSubmitButtonStatus(enabled: boolean): void {
    const submitButton = this.form.querySelector<HTMLButtonElement>(
      'button[type="submit"]',
    );

    if (submitButton) {
      if (enabled) {
        submitButton.innerHTML = this._submitButtonText;
        submitButton.disabled = false;
      } else {
        this._submitButtonText = submitButton.innerHTML;
        submitButton.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${this._submitButtonProcessingText}`;
        submitButton.disabled = true;
      }
    }
  }

  private toggleSocialButtonStatus(enabled: boolean): void {
    const btns = this.form.querySelectorAll<HTMLButtonElement>('.btn-social');
    if (btns) {
      btns.forEach((btn) => {
        btn.disabled = !enabled;
      });
    }
  }

  validate(): boolean {
    this.Logger.trace('validate');
    let isValid = true;
    const inputs = this.form.querySelectorAll('input');
    inputs.forEach((input: HTMLInputElement) => {
      // Clear any existing error message
      this.clearError(input);
      isValid = isValid && this.validateInput(input);
    });
    this.form.classList.add('was-validated');
    this.Logger.info('validate.ISVALID', isValid);
    return isValid;
  }

  private validateInput(input: HTMLInputElement) {
    let isValid = true;

    // Validate required fields
    if (input.hasAttribute('required') && !input.value) {
      this.setError(input, 'required');
      isValid = false;
    }

    // Validate minLength
    if (
      input.hasAttribute('minLength') &&
      input.value.length < Number(input.getAttribute('minLength'))
    ) {
      this.setError(input, 'minLength');
      isValid = false;
    }

    // Validate email
    if (
      input.type === 'email' &&
      input.value &&
      !this.isValidEmail(input.value)
    ) {
      this.setError(input, 'email');
      isValid = false;
    }

    // Validate password match
    if (
      (input.name === 'password' || input.name === 'confirmPassword') &&
      this.form.querySelector('[name="confirmPassword"]')
    ) {
      const password = this.form.querySelector(
        '[name="password"]',
      ) as HTMLInputElement;
      const confirmPassword = this.form.querySelector(
        '[name="confirmPassword"]',
      ) as HTMLInputElement;

      if (input.value.trim() === '') {
        // if value of active input is empty, throw error on the focused input
        this.setError(input, 'required');
        isValid = false;
      } else if (
        password.value !== '' &&
        confirmPassword.value !== '' &&
        password.value !== confirmPassword.value
      ) {
        // if both password inputs have a value, and they don't match, mark both invalid
        this.setError(password, 'passwordMismatch');
        this.setError(confirmPassword, 'passwordMismatch');
        isValid = false;
      } else if (
        password.value !== '' &&
        confirmPassword.value !== '' &&
        password.value === confirmPassword.value
      ) {
        // if both password inputs match, clear errors from both password fields.
        // this will not clear errors when the active input is empty because it would have failed above
        this.clearError(password);
        this.clearError(confirmPassword);
        password.classList.add('is-valid');
        confirmPassword.classList.add('is-valid');
      }
    }
    //this.Logger.trace("validateInput", isValid);
    if (isValid) {
      if (input.value) {
        input.classList.add('is-valid');
      } else {
        input.classList.remove('is-valid');
      }
    }

    this.updateSubmitButtonState();
    return isValid;
  }

  private updateSubmitButtonState(): void {
    const inputs = this.form.querySelectorAll('input');
    const allValid = Array.from(inputs).every((input: HTMLInputElement) =>
      this.checkInputValidity(input),
    );
    this.toggleSubmitButtonEnabled(allValid);
  }

  private checkInputValidity(input: HTMLInputElement): boolean {
    if (input.hasAttribute('required') && !input.value) {
      return false;
    }

    if (
      input.hasAttribute('minLength') &&
      input.value.length < Number(input.getAttribute('minLength'))
    ) {
      return false;
    }

    if (
      input.type === 'email' &&
      input.value &&
      !this.isValidEmail(input.value)
    ) {
      return false;
    }

    if (
      (input.name === 'password' || input.name === 'confirmPassword') &&
      this.form.querySelector('[name="confirmPassword"]')
    ) {
      const password = this.form.querySelector(
        '[name="password"]',
      ) as HTMLInputElement;
      const confirmPassword = this.form.querySelector(
        '[name="confirmPassword"]',
      ) as HTMLInputElement;

      if (!password.value.trim() || !confirmPassword.value.trim()) {
        return false;
      }

      if (password.value !== confirmPassword.value) {
        return false;
      }
    }

    return true;
  }

  private setError(input: HTMLInputElement, errorType: string) {
    input.classList.remove('is-valid');
    input.classList.add('is-invalid');
    input.setCustomValidity(this.getErrorMessage(errorType, input));
    const errorDiv = input.nextElementSibling as HTMLDivElement;
    if (errorDiv && errorDiv.tagName === 'DIV') {
      errorDiv.textContent = this.getErrorMessage(errorType, input);
      errorDiv.style.display = 'block';
    }
  }

  private clearError(input: HTMLInputElement) {
    input.classList.remove('is-invalid');
    input.setCustomValidity('');
    const errorDiv = input.nextElementSibling as HTMLDivElement;
    //this.Logger.trace("clearError", errorDiv);
    if (errorDiv && errorDiv.tagName === 'DIV') {
      errorDiv.style.display = 'none';
      errorDiv.textContent = '';
    }
  }

  private getErrorMessage(errorType: string, input: HTMLInputElement): string {
    const message = this.errorMessages[errorType];

    // If it's a minLength error, replace the placeholder with the actual minLength value
    if (errorType === 'minLength') {
      const minLength = input.getAttribute('minLength');
      return message.replace('{minLength}', minLength || '0');
    }

    return message;
  }

  private isValidEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  private toggleSubmitButtonEnabled(enabled: boolean): void {
    const formFields = this.form.querySelectorAll<HTMLElement>('.btn-submit');
    formFields.forEach((field) => {
      (field as HTMLButtonElement).disabled = !enabled;
    });
  }
}
